
import { useState } from 'react';

interface CustomFortuneModalProps {
  onClose: () => void;
  onSubmit: (fortune: string) => void;
}

export default function CustomFortuneModal({ onClose, onSubmit }: CustomFortuneModalProps) {
  const [fortune, setFortune] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fortune.trim()) {
      onSubmit(fortune);
      setFortune('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-black border-2 sm:border-4 border-pink-400 rounded-lg p-6 sm:p-8 max-w-md sm:max-w-lg w-full neon-modal-glow animate-scaleIn">
        <h2 className="text-pink-400 font-bold text-lg sm:text-xl md:text-2xl mb-4 sm:mb-6 text-center neon-text-modal" style={{ fontFamily: '"Press Start 2P", monospace' }}>
          <span className="text-xs sm:text-sm">CREATE YOUR FORTUNE</span>
        </h2>

        <form onSubmit={handleSubmit}>
          <textarea
            value={fortune}
            onChange={(e) => setFortune(e.target.value)}
            placeholder="Enter your custom fortune..."
            className="w-full bg-black/60 border-2 border-cyan-400/40 rounded p-3 sm:p-4 text-white text-sm sm:text-base focus:border-cyan-400 focus:outline-none neon-input mb-4 sm:mb-6 min-h-[100px] sm:min-h-[120px]"
            maxLength={200}
          />

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              type="submit"
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-sm sm:text-base uppercase tracking-wider border-2 border-pink-400 hover:scale-105 transition-all duration-300 neon-button whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              <span className="text-[10px] sm:text-xs">Add Fortune</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm sm:text-base uppercase tracking-wider border-2 border-gray-500 hover:scale-105 transition-all duration-300 neon-button whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Press Start 2P", monospace' }}
            >
              <span className="text-[10px] sm:text-xs">Cancel</span>
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }

        .neon-modal-glow {
          box-shadow: 
            0 0 30px rgba(255, 0, 255, 0.5),
            0 0 60px rgba(255, 0, 255, 0.3),
            inset 0 0 20px rgba(255, 0, 255, 0.1);
        }

        .neon-text-modal {
          text-shadow: 
            0 0 10px #ec4899,
            0 0 20px #ec4899;
        }

        .neon-input {
          box-shadow: 
            0 0 10px rgba(0, 255, 255, 0.2),
            inset 0 0 10px rgba(0, 255, 255, 0.1);
        }

        .neon-input:focus {
          box-shadow: 
            0 0 20px rgba(0, 255, 255, 0.4),
            inset 0 0 15px rgba(0, 255, 255, 0.2);
        }

        .neon-button {
          box-shadow: 
            0 0 20px currentColor,
            0 0 40px currentColor,
            inset 0 0 10px rgba(255, 255, 255, 0.2);
        }

        .neon-button:hover {
          box-shadow: 
            0 0 30px currentColor,
            0 0 60px currentColor,
            inset 0 0 20px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
